import appSlice from './appSlice';
import authSlice from './authSlice';
import cartSlice from './cartSlice';
import checkoutSlice from './checkoutSlice';
import creditSlice from './creditSlice';
import { cloneDeep } from 'lodash-es';
import locationSlice from './locationSlice';
import pricesSlice from './pricesSlice';
import profileSlice from './profileSlice';
import specFiltersSlice from './specFiltersSlice';
import storage from 'redux-persist/lib/storage';
import userPreferencesSlice from './userPreferencesSlice';
import { AnyAction, combineReducers, Reducer } from '@reduxjs/toolkit';
import { CartState, RentalProtection, Shift } from '../../types/cart-state';
import { createTransform, persistReducer } from 'redux-persist';
import { PersistedConfigs, PersistedStateKeys } from '../../types/store';
import { PersistPartial } from 'redux-persist/es/persistReducer';

const shiftTransform = createTransform(
  (inboundState: Shift) => {
    const state = cloneDeep(inboundState);
    if (state?.options) {
      state.options = [];
    }
    return state;
  },
  (outboundState: Shift) => {
    const state = cloneDeep(outboundState);
    if (state?.options) {
      state.options = [];
    }
    return state;
  },
  { whitelist: ['shift'] },
);

const rentalProtectionTransform = createTransform(
  (inboundState: RentalProtection) => {
    const state = cloneDeep(inboundState);
    if (state?.options) {
      state.options = [];
    }
    return state;
  },
  (outboundState: RentalProtection) => {
    const state = cloneDeep(outboundState);
    if (state?.options) {
      state.options = [];
    }
    return state;
  },
  { whitelist: ['rentalProtection'] },
);

export const persistedConfigs: PersistedConfigs = {
  [PersistedStateKeys.location]: {
    key: PersistedStateKeys.location,
    storage: storage,
    blacklist: ['status'],
  },
  [PersistedStateKeys.cart]: {
    key: PersistedStateKeys.cart,
    storage: storage,
    blacklist: ['isCartActivated', 'cartModalConfig', 'cartHeaderConfig', 'loadingFlags', 'quote', 'isTaxExempt'],
    transforms: [shiftTransform, rentalProtectionTransform],
  },
  [PersistedStateKeys.checkout]: {
    key: PersistedStateKeys.checkout,
    storage: storage,
  },
  [PersistedStateKeys.credit]: {
    key: PersistedStateKeys.credit,
    storage: storage,
  },
  [PersistedStateKeys.prices]: {
    key: PersistedStateKeys.prices,
    storage: storage,
    blacklist: ['fetchPriceQueue', 'priceCatalog'],
  },
  [PersistedStateKeys.auth]: {
    key: PersistedStateKeys.auth,
    storage: storage,
    whitelist: ['lastLoginStatus'],
  },
};

// Persisted Reducers
// Note: Add persist config to persistReducer function using persistedConfigs object
const persistedReducers = {
  cart: persistReducer(persistedConfigs.cart, cartSlice) as Reducer<
    CartState & PersistPartial,
    AnyAction
  >,
  checkout: persistReducer(persistedConfigs.checkout, checkoutSlice),
  credit: persistReducer(persistedConfigs.credit, creditSlice),
  location: persistReducer(persistedConfigs.location, locationSlice),
  prices: persistReducer(persistedConfigs.prices, pricesSlice),
  auth: persistReducer(persistedConfigs.auth, authSlice),
};

// Non persisted reducers
const nonPersistedReducers = {
  app: appSlice,
  profile: profileSlice,
  userPreferences: userPreferencesSlice,
  specFilters: specFiltersSlice,
};

const rootReducer = combineReducers({
  ...persistedReducers,
  ...nonPersistedReducers,
});

export default rootReducer;
